import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { taskAPI, clientAPI, userAPI, phaseAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, getTaskStatusLabel, getTaskStatusColor, getPhaseLabel } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Search, Filter, CheckCircle2, XCircle, Eye, Upload,
  ClipboardCheck, Clock, AlertTriangle, FileCheck, MoreHorizontal, Trash2, Download, Mail, MailCheck, Edit2, CalendarPlus,
} from 'lucide-react';

const TaskEvidenceList = ({ taskId, isAdmin, onChange }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileRef = React.useRef(null);

  const refresh = useCallback(() => {
    if (!taskId) return;
    setLoading(true);
    taskAPI.getEvidence(taskId)
      .then(res => setFiles(res.data || []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDownload = async (ev) => {
    try {
      const res = await taskAPI.downloadEvidence(ev.id);
      const blob = new Blob([res.data], { type: ev.file_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = ev.file_name || 'evidence';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete "${ev.file_name}"? This cannot be undone.`)) return;
    try {
      await taskAPI.deleteEvidence(ev.id);
      toast.success('Evidence deleted');
      refresh();
      onChange?.();
    } catch { toast.error('Failed to delete'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      try {
        await taskAPI.adminUploadEvidence(taskId, {
          evidence_data: base64,
          evidence_file_name: file.name,
          evidence_file_type: file.type,
        });
        toast.success('Evidence uploaded');
        refresh();
        onChange?.();
      } catch { toast.error('Failed to upload'); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-1">
      {loading && <p className="text-xs text-muted-foreground">Loading files...</p>}
      {!loading && files.length === 0 && <p className="text-xs text-muted-foreground">No files uploaded.</p>}
      {files.map(f => (
        <div key={f.id} className="flex items-center justify-between gap-2 bg-white border rounded px-2 py-1 text-xs" data-testid={`evidence-${f.id}`}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FileCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span className="truncate" title={f.file_name}>{f.file_name}</span>
            <span className="text-muted-foreground text-[10px] shrink-0">({Math.round((f.file_size || 0) / 1024)}KB)</span>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => handleDownload(f)} data-testid={`download-evidence-${f.id}`} title="Download">
              <Download className="w-3.5 h-3.5" />
            </Button>
            {isAdmin && (
              <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600 hover:bg-red-50" onClick={() => handleDelete(f)} data-testid={`delete-evidence-${f.id}`} title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}
      {isAdmin && (
        <div className="pt-2">
          <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="admin-evidence-upload-input" />
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} data-testid="admin-evidence-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Add Evidence (Admin)
          </Button>
        </div>
      )}
    </div>
  );
};

const TasksPage = () => {
  const { user, isAdmin, isClient } = useAuth();
  usePageTitle('Tasks');
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [phases, setPhases] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);

  // Dialogs
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isSummaryActionOpen, setIsSummaryActionOpen] = useState(false);
  const [isAdminEditSummaryOpen, setIsAdminEditSummaryOpen] = useState(false);
  const [adminSummaryText, setAdminSummaryText] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [summaryText, setSummaryText] = useState('');
  const [summaryActionRemarks, setSummaryActionRemarks] = useState('');

  // Form state
  const [form, setForm] = useState({ client_id: '', phase_id: '', title: '', description: '', assigned_to: '', due_date: '', priority: 'medium' });
  const [completeForm, setCompleteForm] = useState({ completion_remarks: '', evidence_data: null, evidence_file_name: '', evidence_file_type: '' });
  const [verifyForm, setVerifyForm] = useState({ action: 'verify', verification_remarks: '', completion_summary: '' });

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [tasksRes, clientsRes, staffRes] = await Promise.all([
        taskAPI.getAll(params),
        isClient() ? Promise.resolve({ data: [] }) : clientAPI.getAll(),
        userAPI.lookup({ role: 'staff' }).catch(() => ({ data: [] })),
      ]);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isClient]);

  // Default assignable users = all staff+managers (no client picked yet).
  // When a client is picked we replace with the client-specific list (which adds client users).
  useEffect(() => {
    if (!form.client_id && staff && staff.length > 0) {
      setAssignableUsers(staff.map(s => ({ id: s.id, name: s.name, role: s.role, type: 'consultant' })));
    }
  }, [staff, form.client_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Deep-link support: open detail when ?id=... is passed
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const taskId = searchParams.get('id');
    if (taskId && tasks.length > 0) {
      const t = tasks.find(x => x.id === taskId);
      if (t) {
        setSelectedTask(t);
        setIsDetailOpen(true);
      }
    }
  }, [searchParams, tasks]);

  const loadPhasesForClient = async (clientId) => {
    try {
      const res = await phaseAPI.getAll({ client_id: clientId });
      // Dedupe by phase_type to prevent duplicate React keys
      const seen = new Set();
      const deduped = (res.data || []).filter(p => {
        if (seen.has(p.phase_type)) return false;
        seen.add(p.phase_type);
        return true;
      });
      setPhases(deduped);
      // Auto-select the "in_progress" phase. Fallback: first non-completed phase, else first phase.
      const inProgress = deduped.find(p => p.status === 'in_progress');
      const fallback = inProgress
        || deduped.find(p => p.status !== 'completed' && p.status !== 'blocked')
        || deduped[0];
      if (fallback) {
        setForm(f => ({ ...f, phase_id: fallback.id }));
      }
    } catch { setPhases([]); }
  };

  const loadAssignableUsers = async (clientId) => {
    try {
      const res = await taskAPI.getAssignableUsers(clientId);
      setAssignableUsers(res.data);
    } catch { setAssignableUsers([]); }
  };

  const handleClientChange = (clientId) => {
    setForm(f => ({ ...f, client_id: clientId, phase_id: '', assigned_to: '' }));
    loadPhasesForClient(clientId);
    loadAssignableUsers(clientId);
  };

  // Inline status change from the table — allowed only for assignee or admin/manager/super-admin
  const canChangeStatusInline = (task) => {
    if (!task) return false;
    if (isAdmin()) return true; // covers super_admin + admin + manager via isAdmin()
    return task.assigned_to === user?.id;
  };

  // Allowed transitions for inline change (only forward / safe). Admin sees more options.
  const inlineStatusOptions = (task) => {
    const role = user?.role;
    const isStaffSide = ['super_admin', 'admin', 'manager', 'staff'].includes(role);
    if (!isStaffSide) return [];
    const opts = [];
    // Always include current as no-op
    opts.push({ value: task.status, label: getTaskStatusLabel(task.status) });
    // Forward transitions
    if (task.status === 'open') {
      opts.push({ value: 'in_progress', label: 'In Progress' });
      // Staff can self-mark completed (which routes through completed_by_assignee flow normally)
      opts.push({ value: 'completed_by_assignee', label: 'Mark Completed' });
    } else if (task.status === 'in_progress') {
      opts.push({ value: 'open', label: 'Re-open' });
      opts.push({ value: 'completed_by_assignee', label: 'Mark Completed' });
    } else if (task.status === 'rejected') {
      opts.push({ value: 'in_progress', label: 'Resume (In Progress)' });
    }
    // Dedupe by value
    const seen = new Set();
    return opts.filter(o => seen.has(o.value) ? false : (seen.add(o.value), true));
  };

  const handleInlineStatusChange = async (task, newStatus) => {
    if (newStatus === task.status) return;
    // If user is the assignee and trying to mark complete, open the proper Complete dialog (so they fill summary)
    if (newStatus === 'completed_by_assignee' && task.assigned_to === user?.id && !isAdmin()) {
      openComplete(task);
      return;
    }
    try {
      const payload = { status: newStatus };
      if (newStatus === 'completed_by_assignee') {
        // Admin shortcut completion — stamp completion fields
        payload.completed_at = new Date().toISOString();
      }
      await taskAPI.update(task.id, payload);
      toast.success('Status updated');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await taskAPI.create(form);
      toast.success('Task created');
      setIsCreateOpen(false);
      setForm({ client_id: '', phase_id: '', title: '', description: '', assigned_to: '', due_date: '', priority: 'medium' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleComplete = async () => {
    try {
      await taskAPI.complete(selectedTask.id, completeForm);
      toast.success('Task marked as completed! Pending verification.');
      setIsCompleteOpen(false);
      setCompleteForm({ completion_remarks: '', evidence_data: null, evidence_file_name: '', evidence_file_type: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    }
  };

  const handleVerify = async () => {
    try {
      await taskAPI.verify(selectedTask.id, verifyForm);
      toast.success(verifyForm.action === 'verify' ? 'Task verified & approved!' : 'Task rejected - rework required');
      setIsVerifyOpen(false);
      setVerifyForm({ action: 'verify', verification_remarks: '', completion_summary: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify task');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setCompleteForm(f => ({ ...f, evidence_data: base64, evidence_file_name: file.name, evidence_file_type: file.type }));
    };
    reader.readAsDataURL(file);
  };

  const canComplete = (task) => task.assigned_to === user?.id && ['open', 'in_progress', 'rejected', 'pending'].includes(task.status);
  const canVerify = (task) => {
    if (!['completed_by_assignee'].includes(task.status)) return false;
    if (isAdmin()) return true;
    if (task.assigned_by === user?.id) return true;
    return false;
  };
  const canWriteSummary = (task) => {
    // Only assignee/staff can write summary (admin uses unified Verify & Approve dialog)
    return task.assigned_to === user?.id && !isAdmin() && ['completed_by_assignee', 'verified', 'completed'].includes(task.status) && !task.completion_summary;
  };
  const canApproveSummary = (task) => {
    // Disabled — admin uses single-step Verify & Approve. Only show legacy review for
    // pending_approval summaries on tasks already past verify state.
    return isAdmin() && task.summary_status === 'pending_approval' && task.status === 'verified';
  };

  const handleSubmitSummary = async () => {
    if (!summaryText.trim()) { toast.error('Summary is required'); return; }
    try {
      await taskAPI.submitSummary(selectedTask.id, { summary: summaryText });
      toast.success('Summary submitted for approval');
      setIsSummaryOpen(false); setSummaryText(''); loadData();
    } catch { toast.error('Failed to submit summary'); }
  };

  const handleSummaryAction = async (action) => {
    try {
      await taskAPI.approveSummary(selectedTask.id, { action, remarks: summaryActionRemarks });
      toast.success(action === 'approve' ? 'Summary approved & sent to client' : 'Summary rejected');
      setIsSummaryActionOpen(false); setSummaryActionRemarks(''); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleSendSummaryToClient = async (task) => {
    if (!window.confirm(`Send the approved summary to client (${task.client_name || 'N/A'})?`)) return;
    try {
      const res = await taskAPI.sendSummaryToClient(task.id);
      toast.success(res.data.message || 'Summary sent to client');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send summary to client');
    }
  };

  const handleDownloadSummary = async (task) => {
    try {
      const res = await taskAPI.downloadSummary(task.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Task_Summary_${task.title?.replace(/\s+/g, '_') || task.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Summary downloaded');
    } catch {
      toast.error('Failed to download summary');
    }
  };

  const openDetail = (task) => { setSelectedTask(task); setIsDetailOpen(true); };
  const openComplete = (task) => { setSelectedTask(task); setIsCompleteOpen(true); };
  const openVerify = (task) => {
    setSelectedTask(task);
    setIsVerifyOpen(true);
    setVerifyForm({
      action: 'verify',
      verification_remarks: '',
      completion_summary: task.completion_summary || '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await taskAPI.delete(id); toast.success('Task deleted'); setSelectedIds([]); loadData(); } catch { toast.error('Failed'); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected task(s)? This cannot be undone.`)) return;
    try {
      // Delete in batches of 50 to avoid request size limits
      const batchSize = 50;
      let totalDeleted = 0;
      for (let i = 0; i < selectedIds.length; i += batchSize) {
        const batch = selectedIds.slice(i, i + batchSize);
        const res = await taskAPI.bulkDelete(batch);
        totalDeleted += parseInt(res.data?.message) || batch.length;
      }
      toast.success(`${totalDeleted} tasks deleted`);
      setSelectedIds([]);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown error';
      toast.error(`Failed to delete tasks: ${detail}`);
      console.error('Bulk delete error:', err.response?.status, err.response?.data);
      loadData();
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredTasks.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredTasks.map(t => t.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredTasks = tasks.filter(t =>
    t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.assigned_to_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const openCount = tasks.filter(t => ['open', 'pending'].includes(t.status)).length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const pendingVerification = tasks.filter(t => t.status === 'completed_by_assignee').length;
  const verifiedCount = tasks.filter(t => ['verified', 'completed'].includes(t.status)).length;

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 via-purple-500 to-fuchsia-500 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Task Management</h1>
            <p className="text-sm text-violet-100">Manage tasks with verification workflow</p>
          </div>
          {!isClient() && (
            <Button onClick={() => setIsCreateOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="create-task-btn">
              <Plus className="w-4 h-4 mr-2" /> Assign Task
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-slate-50 to-gray-100"><CardContent className="pt-5 pb-4 text-center"><p className="text-2xl font-bold text-slate-700">{openCount}</p><p className="text-xs text-slate-500 font-medium">Open</p></CardContent></Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50"><CardContent className="pt-5 pb-4 text-center"><p className="text-2xl font-bold text-blue-700">{inProgressCount}</p><p className="text-xs text-blue-500 font-medium">In Progress</p></CardContent></Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50"><CardContent className="pt-5 pb-4 text-center"><p className="text-2xl font-bold text-amber-700">{pendingVerification}</p><p className="text-xs text-amber-500 font-medium">Pending Verification</p></CardContent></Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50"><CardContent className="pt-5 pb-4 text-center"><p className="text-2xl font-bold text-emerald-700">{verifiedCount}</p><p className="text-xs text-emerald-500 font-medium">Verified</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search tasks..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="task-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="task-status-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed_by_assignee">Completed by Assignee</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected / Rework</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar (Admin only) */}
      {isAdmin() && selectedIds.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-red-700">{selectedIds.length} selected</span>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>Clear</Button>
        </div>
      )}

      {/* Task Table (horizontal scrollable) */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[1200px]">
            <TableHeader>
              <TableRow>
                {isAdmin() && (
                  <TableHead className="w-10">
                    <Checkbox checked={filteredTasks.length > 0 && selectedIds.length === filteredTasks.length} onCheckedChange={toggleSelectAll} data-testid="select-all-tasks" />
                  </TableHead>
                )}
                <TableHead>Task</TableHead>
                {!isClient() && <TableHead>Client</TableHead>}
                <TableHead>Assigned To</TableHead>
                <TableHead>Assigned By</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No tasks found</TableCell></TableRow>
              ) : filteredTasks.map(task => (
                <TableRow key={task.id} className="cursor-pointer hover:bg-muted/50" data-testid={`task-row-${task.id}`}>
                  {isAdmin() && (
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Checkbox checked={selectedIds.includes(task.id)} onCheckedChange={() => toggleSelect(task.id)} data-testid={`task-select-${task.id}`} />
                    </TableCell>
                  )}
                  <TableCell className="font-medium max-w-[200px] truncate" onClick={() => openDetail(task)}>{task.title}</TableCell>
                  {!isClient() && <TableCell className="text-sm">{task.client_name || '-'}</TableCell>}
                  <TableCell className="text-sm">{task.assigned_to_name || '-'}</TableCell>
                  <TableCell className="text-sm">{task.assigned_by_name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'default' : 'secondary'}>
                      {task.priority}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{task.due_date ? formatDate(task.due_date) : '-'}</TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {canChangeStatusInline(task) && inlineStatusOptions(task).length > 1 ? (
                      <Select
                        value={task.status}
                        onValueChange={v => handleInlineStatusChange(task, v)}
                      >
                        <SelectTrigger
                          className={`h-7 px-2 py-0 text-xs font-medium w-auto min-w-[110px] border-0 ${getTaskStatusColor(task.status)}`}
                          data-testid={`task-inline-status-${task.id}`}
                        >
                          <SelectValue>{getTaskStatusLabel(task.status)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {inlineStatusOptions(task).map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={getTaskStatusColor(task.status)}>{getTaskStatusLabel(task.status)}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(task)} data-testid={`task-view-${task.id}`}><Eye className="w-4 h-4" /></Button>
                      {canComplete(task) && (
                        <Button variant="ghost" size="sm" onClick={() => openComplete(task)} className="text-amber-600" data-testid={`task-complete-${task.id}`}>
                          <Upload className="w-4 h-4" />
                        </Button>
                      )}
                      {canVerify(task) && (
                        <Button variant="ghost" size="sm" onClick={() => openVerify(task)} className="text-green-600" data-testid={`task-verify-${task.id}`}>
                          <ClipboardCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {task.due_date && (
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Add to Google/Outlook Calendar (.ics)"
                          className="text-indigo-600"
                          data-testid={`task-ics-${task.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await taskAPI.downloadIcs(task.id);
                              const blob = new Blob([res.data], { type: 'text/calendar' });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `task-${task.id.slice(0,8)}.ics`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                              toast.success('Calendar file downloaded');
                            } catch (err) {
                              toast.error('Failed to download');
                            }
                          }}
                        >
                          <CalendarPlus className="w-4 h-4" />
                        </Button>
                      )}
                      {canWriteSummary(task) && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedTask(task); setSummaryText(''); setIsSummaryOpen(true); }} className="text-blue-600" title="Write Summary">
                          <FileCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {canApproveSummary(task) && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedTask(task); setIsSummaryActionOpen(true); }} className="text-purple-600" title="Review Summary">
                          <ClipboardCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {(task.summary_status === 'approved' || task.status === 'verified') && isAdmin() && !task.summary_sent_to_client && task.client_id && (
                        <Button variant="ghost" size="sm" onClick={() => handleSendSummaryToClient(task)} className="text-orange-600" title="Send Summary to Client" data-testid={`task-send-client-${task.id}`}>
                          <Mail className="w-4 h-4" />
                        </Button>
                      )}
                      {task.summary_sent_to_client && (
                        <span className="inline-flex items-center text-emerald-600 text-xs px-1" title={`Sent ${task.summary_sent_at ? formatDate(task.summary_sent_at) : ''}`}>
                          <MailCheck className="w-4 h-4" />
                        </span>
                      )}
                      {(task.status === 'completed' || task.status === 'verified' || task.completion_summary || task.summary_status) && (
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadSummary(task)} className="text-teal-600" title="Download Summary PDF" data-testid={`task-download-summary-${task.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin() && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(task.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign New Task</DialogTitle>
            <DialogDescription>Create and assign a task to a consultant or client</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client (optional)</Label>
                <Select value={form.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger data-testid="task-form-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phase {form.client_id && phases.length === 0 && <span className="text-xs text-amber-600">(no phases — pick a client first)</span>}</Label>
                <Select value={form.phase_id} onValueChange={v => setForm(f => ({ ...f, phase_id: v }))} disabled={!form.client_id || phases.length === 0}>
                  <SelectTrigger data-testid="task-form-phase"><SelectValue placeholder={form.client_id ? "Select phase" : "Pick a client first"} /></SelectTrigger>
                  <SelectContent>
                    {phases.map(p => {
                      const isInProgress = p.status === 'in_progress';
                      const isCompleted = p.status === 'completed';
                      const isBlocked = p.status === 'blocked';
                      return (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="flex items-center gap-2">
                            <span>{getPhaseLabel(p.phase_type)}</span>
                            {isInProgress && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">IN PROGRESS</span>}
                            {isCompleted && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700">DONE</span>}
                            {isBlocked && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">BLOCKED</span>}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="task-form-title" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger data-testid="task-form-assignee"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    {assignableUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.type === 'client' ? 'Client' : 'Consultant'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="task-form-submit">Create Task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTask?.title}</DialogTitle>
            <DialogDescription>Task Details</DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="font-medium text-muted-foreground">Status:</span> <Badge className={getTaskStatusColor(selectedTask.status)}>{getTaskStatusLabel(selectedTask.status)}</Badge></div>
                <div><span className="font-medium text-muted-foreground">Priority:</span> <Badge variant={selectedTask.priority === 'high' ? 'destructive' : 'default'}>{selectedTask.priority}</Badge></div>
                <div><span className="font-medium text-muted-foreground">Client:</span> {selectedTask.client_name || '-'}</div>
                <div><span className="font-medium text-muted-foreground">Due Date:</span> {selectedTask.due_date ? formatDate(selectedTask.due_date) : '-'}</div>
                <div><span className="font-medium text-muted-foreground">Assigned By:</span> {selectedTask.assigned_by_name || '-'}</div>
                <div><span className="font-medium text-muted-foreground">Assigned To:</span> {selectedTask.assigned_to_name || '-'}</div>
              </div>
              {selectedTask.description && <div><span className="font-medium text-muted-foreground">Description:</span><p className="mt-1">{selectedTask.description}</p></div>}
              {selectedTask.completion_remarks && (
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="font-medium text-amber-800 text-xs mb-1">Completion Remarks</p>
                  <p>{selectedTask.completion_remarks}</p>
                  {selectedTask.completed_at && <p className="text-xs text-muted-foreground mt-1">Completed: {formatDate(selectedTask.completed_at)}</p>}
                </div>
              )}
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="font-medium text-blue-800 text-xs mb-2">Evidence Files {selectedTask.evidence_ids?.length > 0 && `(${selectedTask.evidence_ids.length})`}</p>
                <TaskEvidenceList taskId={selectedTask.id} isAdmin={isAdmin()} onChange={loadData} />
              </div>
              {selectedTask.verification_remarks && (
                <div className={`p-3 rounded-lg border ${selectedTask.status === 'verified' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className={`font-medium text-xs mb-1 ${selectedTask.status === 'verified' ? 'text-green-800' : 'text-red-800'}`}>
                    {selectedTask.status === 'verified' ? 'Verification Remarks' : 'Rejection Remarks'}
                  </p>
                  <p>{selectedTask.verification_remarks}</p>
                  <p className="text-xs text-muted-foreground mt-1">By: {selectedTask.verified_by_name} {selectedTask.verified_at ? `on ${formatDate(selectedTask.verified_at)}` : ''}</p>
                </div>
              )}
              {(selectedTask.completion_summary || isAdmin()) && (
                <div className={`p-3 rounded-lg border max-h-[40vh] overflow-y-auto ${selectedTask.summary_status === 'approved' ? 'bg-green-50 border-green-200' : selectedTask.summary_status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-xs">Completion Summary {selectedTask.summary_status && `(${selectedTask.summary_status.replace('_', ' ')})`}</p>
                    {isAdmin() && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => { setAdminSummaryText(selectedTask.completion_summary || ''); setIsAdminEditSummaryOpen(true); }} data-testid="admin-edit-summary-btn">
                        <Edit2 className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words">{selectedTask.completion_summary || <span className="text-muted-foreground italic">No summary yet — click Edit to add one</span>}</p>
                </div>
              )}
              <div className="flex gap-2 pt-2 flex-wrap">
                {canComplete(selectedTask) && <Button size="sm" variant="outline" onClick={() => { setIsDetailOpen(false); openComplete(selectedTask); }}><Upload className="w-4 h-4 mr-1" /> Mark Complete</Button>}
                {canVerify(selectedTask) && <Button size="sm" onClick={() => { setIsDetailOpen(false); openVerify(selectedTask); }}><ClipboardCheck className="w-4 h-4 mr-1" /> Verify &amp; Approve</Button>}
                {canWriteSummary(selectedTask) && <Button size="sm" variant="outline" onClick={() => { setIsDetailOpen(false); setSelectedTask(selectedTask); setSummaryText(''); setIsSummaryOpen(true); }}><FileCheck className="w-4 h-4 mr-1" /> Write Summary</Button>}
                {canApproveSummary(selectedTask) && <Button size="sm" onClick={() => { setIsDetailOpen(false); setSelectedTask(selectedTask); setIsSummaryActionOpen(true); }} className="bg-purple-600 hover:bg-purple-700"><ClipboardCheck className="w-4 h-4 mr-1" /> Review Summary</Button>}
                {(selectedTask.summary_status === 'approved' || selectedTask.status === 'verified') && isAdmin() && !selectedTask.summary_sent_to_client && selectedTask.client_id && (
                  <Button size="sm" variant="default" onClick={() => { setIsDetailOpen(false); handleSendSummaryToClient(selectedTask); }} className="bg-orange-600 hover:bg-orange-700" data-testid="send-task-summary-to-client-btn">
                    <Mail className="w-4 h-4 mr-1" /> Send Summary to Client
                  </Button>
                )}
                {selectedTask.summary_sent_to_client && (
                  <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1"><MailCheck className="w-3.5 h-3.5" /> Summary sent to client {selectedTask.summary_sent_at ? `on ${formatDate(selectedTask.summary_sent_at)}` : ''}</Badge>
                )}
                {selectedTask.completion_summary && selectedTask.summary_status === 'approved' && (
                  <Button size="sm" variant="outline" onClick={() => handleDownloadSummary(selectedTask)} data-testid="download-task-summary-btn"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete Task Dialog */}
      <Dialog open={isCompleteOpen} onOpenChange={setIsCompleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Task</DialogTitle>
            <DialogDescription>Mark "{selectedTask?.title}" as completed and upload evidence</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Completion Remarks</Label>
              <Textarea value={completeForm.completion_remarks} onChange={e => setCompleteForm(f => ({ ...f, completion_remarks: e.target.value }))} rows={3} placeholder="Describe what was done..." data-testid="complete-remarks" />
            </div>
            <div className="space-y-2">
              <Label>Upload Evidence (document/photo)</Label>
              <Input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileChange} data-testid="complete-evidence-upload" />
              {completeForm.evidence_file_name && <p className="text-xs text-green-600">File: {completeForm.evidence_file_name}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCompleteOpen(false)}>Cancel</Button>
            <Button onClick={handleComplete} data-testid="complete-submit-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Submit for Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify & Approve Task Dialog */}
      <Dialog open={isVerifyOpen} onOpenChange={setIsVerifyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify &amp; Approve Task</DialogTitle>
            <DialogDescription>Review, approve, and close "{selectedTask?.title}" in one step</DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              {selectedTask.completion_remarks && (
                <div className="bg-muted p-3 rounded-lg">
                  <p className="font-medium text-xs mb-1">Assignee's Remarks:</p>
                  <p className="text-sm">{selectedTask.completion_remarks}</p>
                </div>
              )}
              {selectedTask.evidence_ids?.length > 0 && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm">📎 {selectedTask.evidence_ids.length} evidence file(s) uploaded — will be attached when sending to client</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={verifyForm.action} onValueChange={v => setVerifyForm(f => ({ ...f, action: v }))}>
                  <SelectTrigger data-testid="verify-action"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verify">✓ Verify &amp; Approve (close task)</SelectItem>
                    <SelectItem value="reject">✗ Reject / Rework Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Verification Remarks</Label>
                <Textarea value={verifyForm.verification_remarks} onChange={e => setVerifyForm(f => ({ ...f, verification_remarks: e.target.value }))} rows={2} placeholder="Comments on the work..." data-testid="verify-remarks" />
              </div>
              {verifyForm.action === 'verify' && (
                <div className="space-y-2">
                  <Label>Final Summary for Client (optional)</Label>
                  <Textarea
                    value={verifyForm.completion_summary}
                    onChange={e => setVerifyForm(f => ({ ...f, completion_summary: e.target.value }))}
                    rows={5}
                    placeholder="Enter a clean summary that will be sent to the client when you click 'Send to Client'..."
                    data-testid="verify-summary-text"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: If staff has already written a summary, it appears here pre-filled — edit and finalize.
                    Once verified, the task is marked closed. Use the orange "Send Summary to Client" button to email it (with attachments).
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsVerifyOpen(false)}>Cancel</Button>
            <Button onClick={handleVerify} variant={verifyForm.action === 'verify' ? 'default' : 'destructive'} data-testid="verify-submit-btn">
              {verifyForm.action === 'verify' ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Verify &amp; Approve</> : <><XCircle className="w-4 h-4 mr-1" /> Reject</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Dialog */}
      <Dialog open={isSummaryOpen} onOpenChange={setIsSummaryOpen}>
        <DialogContent className="max-w-lg" data-testid="summary-dialog">
          <DialogHeader>
            <DialogTitle>Write Task Summary</DialogTitle>
            <DialogDescription>Write a summary for: {selectedTask?.title}</DialogDescription>
          </DialogHeader>
          <Textarea value={summaryText} onChange={e => setSummaryText(e.target.value)} placeholder="Describe what was accomplished, key findings, next steps..." rows={5} data-testid="summary-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSummaryOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitSummary} data-testid="submit-summary-btn">Submit for Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Approve/Reject Dialog */}
      <Dialog open={isSummaryActionOpen} onOpenChange={setIsSummaryActionOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="summary-action-dialog">
          <DialogHeader>
            <DialogTitle>Review Task Summary</DialogTitle>
            <DialogDescription>{selectedTask?.title}</DialogDescription>
          </DialogHeader>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-sm whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">{selectedTask?.completion_summary || 'No summary'}</div>
          <div className="space-y-2">
            <Label>Remarks (optional, for rejection)</Label>
            <Textarea value={summaryActionRemarks} onChange={e => setSummaryActionRemarks(e.target.value)} rows={2} placeholder="Reason for rejection..." />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsSummaryActionOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleSummaryAction('reject')}>Reject</Button>
            <Button onClick={() => handleSummaryAction('approve')} data-testid="approve-summary-btn">Approve (admin will send to client)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Summary Dialog */}
      <Dialog open={isAdminEditSummaryOpen} onOpenChange={setIsAdminEditSummaryOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-edit-summary-dialog">
          <DialogHeader>
            <DialogTitle>Edit Task Summary</DialogTitle>
            <DialogDescription>Admin edit — your changes will overwrite the existing summary for: {selectedTask?.title}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={adminSummaryText}
            onChange={e => setAdminSummaryText(e.target.value)}
            rows={8}
            placeholder="Edit the completion summary..."
            data-testid="admin-edit-summary-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAdminEditSummaryOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                await taskAPI.adminEditSummary(selectedTask.id, { completion_summary: adminSummaryText });
                toast.success('Summary updated');
                setIsAdminEditSummaryOpen(false);
                loadData();
                // Refresh selected task in detail
                const updated = await taskAPI.getOne(selectedTask.id);
                setSelectedTask(updated.data);
              } catch { toast.error('Failed to update summary'); }
            }} data-testid="save-admin-summary-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
